// frontend/src/pages/recruiter/EditJobForm.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "./../../components/ui/button";
import { Input } from "./../../components/ui/input";
import { Label } from "./../../components/ui/label";
import { Textarea } from "./../../components/ui/textarea";
import { Alert, AlertDescription } from "./../../components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./../../components/ui/dialog";
import { Loader2, ArrowLeft, Trash2, Shield } from "lucide-react";
import api from "./../../api/client";
import { Switch } from "./../../components/ui/switch";

export default function EditJobForm() {
 const { jobId } = useParams();
 const navigate = useNavigate();
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(true);
 const [error, setError] = useState("");
 const [success, setSuccess] = useState("");
 const [deleteOpen, setDeleteOpen] = useState(false);

 const [formData, setFormData] = useState({
 title: "",
 description: "",
 requirements: "",
 category: "",
 location: "",
 salaryMin: "",
 salaryMax: "",
 deadline: "",
 tags: "",
 adminCanView: false,
 });

 useEffect(() => {
 fetchJob();
 }, [jobId]);

 const fetchJob = async () => {
 try {
 const res = await api.get(`/jobs/${jobId}`);
 const job = res.data;
 setFormData({
 title: job.title,
 description: job.description,
 requirements: job.requirements,
 category: job.category,
 location: job.location,
 salaryMin: job.salaryMin || "",
 salaryMax: job.salaryMax || "",
 deadline: job.deadline ? new Date(job.deadline).toISOString().split("T")[0] : "",
 tags: job.tags?.join(", ") || "",
 adminCanView: !!job.adminCanView,
 });
 } catch (err) {
 setError("Failed to load job");
 } finally {
 setFetching(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);
 setError("");
 setSuccess("");

 try {
 await api.patch(`/recruiter/jobs/${jobId}`, {
 ...formData,
 salaryMin: formData.salaryMin ? Number(formData.salaryMin) : null,
 salaryMax: formData.salaryMax ? Number(formData.salaryMax) : null,
 deadline: formData.deadline ? new Date(formData.deadline) : null,
 tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
 });
 setSuccess("Job updated successfully!");
 setTimeout(() => navigate("/recruiter/dashboard"), 2000);
 } catch (err) {
 setError(err.response?.data?.message || "Failed to update job");
 } finally {
 setLoading(false);
 }
 };

 const handleDelete = async () => {
 setLoading(true);
 try {
 await api.delete(`/recruiter/jobs/${jobId}`);
 setSuccess("Job deleted");
 setTimeout(() => navigate("/recruiter/dashboard"), 1500);
 } catch (err) {
 setError(err.response?.data?.message || "Failed to delete");
 } finally {
 setLoading(false);
 setDeleteOpen(false);
 }
 };

 if (fetching) {
 return (
 <div className="min-h-screen flex items-center justify-center">
 
 <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
 </div>
 );
 }

 return (
 <div className="min-h-screen py-10 px-4">
 
 <div className="max-w-3xl mx-auto space-y-8">
 <div className="flex items-center justify-between">
 <Button variant="outline" className="rounded-xl" onClick={() => navigate(-1)}>
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back
 </Button>

 <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
 <DialogTrigger asChild>
 <Button variant="destructive" size="sm" className="rounded-xl shadow-md">
 <Trash2 className="w-4 h-4 mr-1" />
 Delete Job
 </Button>
 </DialogTrigger>
 <DialogContent className="rounded-2xl border-border bg-card">
 <DialogHeader>
 <DialogTitle className="text-xl font-bold">Delete Job?</DialogTitle>
 </DialogHeader>
 <p className="text-muted-foreground leading-relaxed">
 This action is permanent and cannot be undone. All data related to this job will be lost.
 </p>
 <div className="flex justify-end gap-3 mt-6">
 <Button variant="outline" className="rounded-lg" onClick={() => setDeleteOpen(false)}>
 Cancel
 </Button>
 <Button variant="destructive" className="rounded-lg px-6" onClick={handleDelete} disabled={loading}>
 {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Delete"}
 </Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>

 <div className="bg-card/50 backdrop-blur-xl border border-border p-6 sm:p-10 rounded-3xl shadow-xl space-y-8">
 <div>
 <h1 className="text-3xl font-bold text-foreground tracking-tight">Edit Job Details</h1>
 <p className="text-muted-foreground mt-2">Refine the job posting to attract the right candidates.</p>
 </div>

 {success && (
 <Alert className="border-green-200 bg-green-500/10/50 dark:border-green-800">
 <AlertDescription className="text-green-800 dark:text-green-300 font-medium">{success}</AlertDescription>
 </Alert>
 )}
 {error && (
 <Alert variant="destructive" className="border-red-200 bg-red-50/50 ">
 <AlertDescription className="font-medium">{error}</AlertDescription>
 </Alert>
 )}

 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="space-y-2">
 <Label htmlFor="title" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Job Title</Label>
 <Input
 id="title"
 className="h-12 rounded-xl /50 border-border focus:ring-2 focus:ring-primary shadow-sm"
 placeholder="e.g. Senior Software Engineer"
 value={formData.title}
 onChange={(e) => setFormData({ ...formData, title: e.target.value })}
 required
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="description" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Description</Label>
 <Textarea
 id="description"
 rows={6}
 className="rounded-xl /50 border-border focus:ring-2 focus:ring-primary shadow-sm"
 placeholder="Describe the role, mission, and daily responsibilities..."
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 required
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="requirements" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Requirements (one per line)</Label>
 <Textarea
 id="requirements"
 rows={5}
 className="rounded-xl /50 border-border focus:ring-2 focus:ring-primary shadow-sm"
 placeholder="List key qualifications and skills..."
 value={formData.requirements}
 onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
 required
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <Label htmlFor="category" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Category</Label>
 <Input
 id="category"
 className="h-12 rounded-xl /50 border-border shadow-sm"
 placeholder="e.g. Engineering"
 value={formData.category}
 onChange={(e) => setFormData({ ...formData, category: e.target.value })}
 required
 />
 </div>
 <div className="space-y-2">
 <Label htmlFor="location" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Location</Label>
 <Input
 id="location"
 className="h-12 rounded-xl /50 border-border shadow-sm"
 placeholder="e.g. remote, Addis Ababa"
 value={formData.location}
 onChange={(e) => setFormData({ ...formData, location: e.target.value })}
 required
 />
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <Label htmlFor="salaryMin" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Min Salary</Label>
 <Input
 id="salaryMin"
 type="number"
 className="h-12 rounded-xl /50 border-border shadow-sm"
 placeholder="0"
 value={formData.salaryMin}
 onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value })}
 />
 </div>
 <div className="space-y-2">
 <Label htmlFor="salaryMax" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Max Salary</Label>
 <Input
 id="salaryMax"
 type="number"
 className="h-12 rounded-xl /50 border-border shadow-sm"
 placeholder="0"
 value={formData.salaryMax}
 onChange={(e) => setFormData({ ...formData, salaryMax: e.target.value })}
 />
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <Label htmlFor="deadline" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Deadline</Label>
 <Input
 id="deadline"
 type="date"
 className="h-12 rounded-xl /50 border-border shadow-sm"
 value={formData.deadline}
 onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
 required
 />
 </div>
 <div className="space-y-2">
 <Label htmlFor="tags" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Tags (comma-separated)</Label>
 <Input
 id="tags"
 className="h-12 rounded-xl /50 border-border shadow-sm"
 placeholder="e.g. react, nodejs, remote"
 value={formData.tags}
 onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
 />
 </div>
 </div>

 <div className="flex items-center justify-between p-6 rounded-2xl border border-indigo-100 bg-indigo-500/10/30 dark:border-indigo-500/10 shadow-sm">
 <div className="space-y-1">
 <Label htmlFor="admin-view" className="text-base font-bold flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
 <Shield className="w-4 h-4" />
 Allow Admin View
 </Label>
 <p className="text-sm text-muted-foreground">
 Grant permission for administrators to monitor applications for this job.
 </p>
 </div>
 <Switch
 id="admin-view"
 checked={formData.adminCanView}
 onCheckedChange={(checked) => setFormData({ ...formData, adminCanView: checked })}
 />
 </div>
 <Button type="submit" disabled={loading} className="w-full h-14 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-lg shadow-xl shadow-indigo-500/20 transition-all hover:scale-[1.01]">
 {loading ? (
 <>
 <Loader2 className="w-5 h-5 mr-3 animate-spin" />
 Saving Changes...
 </>
 ) : (
 "Save and Publish"
 )}
 </Button>
 </form>
 </div>
 </div>
 </div>
 );
}